import React, { useState } from 'react';
import { Avatar, BottomNavigation, BottomNavigationAction, Box, Button, IconButton, Link as MuiLink, Menu, MenuItem, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';
import { SearchRounded, FavoriteBorderRounded, KeyRounded, ChatBubbleOutlineRounded } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import NotificationDropdown from '../../components/common/NotificationDropdown';
import WhatsAppChatbot from '../../components/common/WhatsAppChatbot';
import { MAIN_SITE_URL } from '../../utils/externalUrls';
import { BODY, DISPLAY, M } from './ui';

export const MANAGE_URL = process.env.REACT_APP_MANAGE_URL || 'https://www.manage.dwello.ikxano.com';

interface NavDef { label: string; path: string; match: (p: string) => boolean; icon: React.ReactNode }
const MAIN: NavDef[] = [
  { label: 'Explore', path: '/', match: (p) => p === '/' || p.startsWith('/search') || p.startsWith('/property/') || p.startsWith('/book/'), icon: <SearchRounded fontSize="small" /> },
  { label: 'Saved', path: '/favorites', match: (p) => p.startsWith('/favorites'), icon: <FavoriteBorderRounded fontSize="small" /> },
  { label: 'My home', path: '/my-home', match: (p) => p.startsWith('/my-home'), icon: <KeyRounded fontSize="small" /> },
  { label: 'Messages', path: '/messages', match: (p) => p.startsWith('/messages'), icon: <ChatBubbleOutlineRounded fontSize="small" /> },
];

interface Props {
  children: React.ReactNode;
  maxWidth?: number | 'full';
  headerCenter?: React.ReactNode;
  hideFooter?: boolean;
  gutter?: number;
}

/** Dwello Homes top navigation: logo, Explore / Saved / My home / Messages, "List with Dwello", Sign in. */
const MarketplaceShell: React.FC<Props> = ({ children, maxWidth = 1120, headerCenter, hideFooter, gutter = 64 }) => {
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const [menuEl, setMenuEl] = useState<null | HTMLElement>(null);
  const initials = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.toUpperCase() || 'D';

  const logo = (
    <Box component="a" href="/" onClick={(e: React.MouseEvent) => { e.preventDefault(); navigate('/'); }} aria-label="Dwello Homes home" sx={{ display: 'flex', alignItems: 'center', gap: 1.25, textDecoration: 'none', flexShrink: 0 }}>
      <Box sx={{ width: 34, height: 34, borderRadius: '10px', bgcolor: M.navy, color: M.gold, display: 'grid', placeItems: 'center', fontFamily: DISPLAY, fontWeight: 800, fontSize: 19 }}>D</Box>
      <Typography sx={{ fontFamily: DISPLAY, fontSize: 21, fontWeight: 800, color: M.navy }}>Dwello <Box component="span" sx={{ color: M.gold }}>Homes</Box></Typography>
    </Box>
  );

  const account = isAuthenticated ? (
    <>
      <IconButton onClick={(e) => setMenuEl(e.currentTarget)} aria-label="Your account" aria-haspopup="menu"><Avatar sx={{ width: 40, height: 40, bgcolor: M.navy, color: M.gold, fontWeight: 800, fontSize: 14 }}>{initials}</Avatar></IconButton>
      <Menu anchorEl={menuEl} open={!!menuEl} onClose={() => setMenuEl(null)}>
        {[['Profile', '/profile'], ['Applications', '/applications'], ['Leases', '/leases'], ['My offers', '/my-offers'], ['Saved homes', '/favorites'], ['Messages', '/messages'], ['Payments', '/payments'], ['My reviews', '/my-reviews'], ['Agents', '/agents'], ['Notifications', '/notifications'], ['Map view', '/map'], ['Ask AI', '/insights'], ['Settings', '/settings'], ['Help & Support', '/help']].map(([l, p]) => (
          <MenuItem key={p} onClick={() => { setMenuEl(null); navigate(p); }}>{l}</MenuItem>
        ))}
        <MenuItem onClick={() => { setMenuEl(null); logout(); navigate('/login'); }}>Log out</MenuItem>
      </Menu>
    </>
  ) : (
    <Button onClick={() => navigate('/login')} sx={{ minHeight: 44, px: 2.5, borderRadius: '12px', bgcolor: M.navy, color: '#fff', textTransform: 'none', fontWeight: 700, fontSize: 15, '&:hover': { bgcolor: M.navyDark } }}>Sign in</Button>
  );

  if (!desktop) {
    const idx = MAIN.findIndex((n) => n.match(pathname));
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: M.bg, color: M.text, fontFamily: BODY, pb: 'calc(72px + env(safe-area-inset-bottom))' }}>
        <Box sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, pb: 1.25, pt: 'calc(10px + env(safe-area-inset-top))', pl: 'max(16px, env(safe-area-inset-left))', pr: 'max(16px, env(safe-area-inset-right))', borderBottom: `1px solid ${M.border}` }}>
          {logo}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>{isAuthenticated && <NotificationDropdown />}{account}</Box>
        </Box>
        <Box>{children}</Box>
        <Paper elevation={8} sx={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 20, pb: 'env(safe-area-inset-bottom)', pl: 'env(safe-area-inset-left)', pr: 'env(safe-area-inset-right)' }}>
          <BottomNavigation showLabels value={idx === -1 ? false : idx} onChange={(_, v) => navigate(MAIN[v].path)} sx={{ height: 64 }}>
            {MAIN.map((n) => <BottomNavigationAction key={n.path} label={n.label} icon={n.icon} sx={{ minWidth: 0, '&.Mui-selected': { color: M.navy } }} />)}
          </BottomNavigation>
        </Paper>
        <WhatsAppChatbot />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: M.bg, color: M.text, fontFamily: BODY }}>
      <Box component="header" sx={{ position: 'sticky', top: 0, zIndex: 20, display: 'flex', alignItems: 'center', gap: 5, height: 72, px: `${gutter}px`, bgcolor: '#fff', borderBottom: `1px solid ${M.border}`, flexShrink: 0 }}>
        {logo}
        {headerCenter}
        <Box component="nav" aria-label="Main" sx={{ display: 'flex', gap: 0.75, ...(headerCenter ? { ml: 'auto' } : { flexGrow: 1 }) }}>
          {MAIN.map((n) => {
            const active = n.match(pathname);
            return (
              <Box key={n.path} component="a" href={n.path} aria-current={active ? 'page' : undefined}
                onClick={(e: React.MouseEvent) => { if (e.metaKey || e.ctrlKey) return; e.preventDefault(); navigate(n.path); }}
                sx={{ px: 1.75, py: 1.25, borderRadius: '12px', bgcolor: active ? M.navTint : 'transparent', fontSize: 15, fontWeight: active ? 700 : 500, color: active ? M.text : M.text3, textDecoration: 'none', '&:hover': { bgcolor: active ? M.navTint : '#F6F4EE' } }}>{n.label}</Box>
            );
          })}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
          <MuiLink href={MANAGE_URL} underline="none" sx={{ fontSize: 15, fontWeight: 700, color: M.navy, whiteSpace: 'nowrap' }}>List with Dwello</MuiLink>
          {isAuthenticated && <NotificationDropdown />}
          {account}
        </Box>
      </Box>
      <Box component="main" sx={{ flex: 1, minWidth: 0 }}>
        {maxWidth === 'full' ? children : <Box sx={{ maxWidth, mx: 'auto', px: { md: 4, lg: `${gutter}px` }, py: 5 }}>{children}</Box>}
      </Box>
      {!hideFooter && (
        <Box component="footer" sx={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', px: `${gutter}px`, py: 2.75, bgcolor: M.navy, color: '#C9D3E3', fontSize: 15 }}>
          <Typography sx={{ fontFamily: DISPLAY, fontSize: 18, fontWeight: 800, color: '#fff' }}>Dwello Homes</Typography>
          <MuiLink href={MAIN_SITE_URL} underline="none" sx={{ color: '#fff' }}>Dwello for students</MuiLink>
          <MuiLink href="/?type=SALE" underline="none" sx={{ color: '#fff' }}>Property for sale</MuiLink>
          <MuiLink href={MANAGE_URL} underline="none" sx={{ color: '#fff' }}>Dwello Manage for landlords and agents</MuiLink>
          <Box sx={{ ml: 'auto' }}>WhatsApp 055 111 0949</Box>
        </Box>
      )}
      <WhatsAppChatbot />
    </Box>
  );
};

export default MarketplaceShell;
